import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface RecentSession {
  id: string;
  device_name: string;
  status: string;
  verification_code: number;
  verification_phrase?: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
}

interface VenueDevice {
  id: string;
  name: string;
  status: string;
  last_heartbeat_at?: string;
  created_at: string;
}

const STALE_HEARTBEAT_MS = 5 * 60 * 1000; // 5 minutes

function statusColor(status: string): string {
  switch (status) {
    case "online":
      return "bg-green-500";
    case "provisioned":
      return "bg-yellow-500";
    default:
      return "bg-gray-500";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "online":
      return "Online";
    case "provisioned":
      return "Provisioned";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isStale(device: VenueDevice): boolean {
  if (!device.last_heartbeat_at) return true;
  return Date.now() - new Date(device.last_heartbeat_at).getTime() > STALE_HEARTBEAT_MS;
}

function sessionStatusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "expired":
      return (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Expired
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
          Cancelled
        </span>
      );
    case "awaiting_payment":
      return (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
          Awaiting
        </span>
      );
    default:
      return (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

function RepairModal({
  device,
  venueId,
  onClose,
  onRepaired,
}: {
  device: VenueDevice;
  venueId: string;
  onClose: () => void;
  onRepaired: () => void;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) return;

      setSubmitting(true);
      setError(null);

      try {
        const res = await apiClient["/api/v1/venue/devices/{id}/re-pair"]["post"]({
          parameters: { id: device.id, venue_id: venueId },
          body: { code: trimmed },
        });
        if (res.code === 200) {
          onRepaired();
        } else {
          const body = res.body as { message?: string };
          setError(body.message ?? "Re-pair failed");
        }
      } catch {
        setError("Network error");
      } finally {
        setSubmitting(false);
      }
    },
    [code, device.id, venueId, onRepaired],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface-raised border border-border rounded-lg p-6 w-96 max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-text-primary mb-1">Re-pair Device</h2>
        <p className="text-sm text-text-secondary mb-4">
          Enter the setup code shown on the new device to re-link{" "}
          <span className="font-medium text-text-primary">{device.name}</span>.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. ABC123"
            maxLength={6}
            className="w-full px-4 py-3 rounded-md bg-surface border border-border text-text-primary text-center text-xl font-mono tracking-widest uppercase placeholder:text-text-muted placeholder:tracking-normal placeholder:text-base placeholder:font-sans focus:outline-none focus:border-accent"
            autoFocus
          />

          {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-md text-text-secondary hover:text-text-primary transition-colors border border-border"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || code.trim().length < 6}
              className="flex-1 px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Re-pairing..." : "Re-pair"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UnpairModal({
  device,
  venueId,
  onClose,
  onUnpaired,
}: {
  device: VenueDevice;
  venueId: string;
  onClose: () => void;
  onUnpaired: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnpair = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    try {
      const res = await apiClient["/api/v1/venue/devices/{id}"]["delete"]({
        parameters: { id: device.id, venue_id: venueId },
        body: null,
      });
      if (res.code === 200) {
        onUnpaired();
      } else {
        const body = res.body as { message?: string };
        setError(body.message ?? "Unpair failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }, [device.id, venueId, onUnpaired]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface-raised border border-border rounded-lg p-6 w-96 max-w-[90vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-text-primary mb-1">Unpair Device</h2>
        <p className="text-sm text-text-secondary mb-4">
          Are you sure you want to unpair{" "}
          <span className="font-medium text-text-primary">{device.name}</span>? The device will be
          disconnected and must be set up again to use.
        </p>

        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm rounded-md text-text-secondary hover:text-text-primary transition-colors border border-border"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUnpair}
            disabled={submitting}
            className="flex-1 px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Unpairing..." : "Unpair"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FreePlayStatus {
  timezone: string;
  recurring: { day: number; start: string; end: string }[];
  adhoc: { starts_at: string; expires_at: string }[];
  is_free_play: boolean;
  free_play_ends_at?: string;
}

function formatFreePlayRemaining(endsAt: string): string {
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return "ending now";
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `${minutes}m remaining`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m remaining` : `${hours}h remaining`;
}

function formatVenueDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function venueStatusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "inactive":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Inactive
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

export function Home() {
  const { selectedVenue } = useAuth();
  const [devices, setDevices] = useState<VenueDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [repairTarget, setRepairTarget] = useState<VenueDevice | null>(null);
  const [unpairTarget, setUnpairTarget] = useState<VenueDevice | null>(null);
  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [freePlayStatus, setFreePlayStatus] = useState<FreePlayStatus | null>(null);
  const [freePlayAdding, setFreePlayAdding] = useState(false);
  const [freePlayError, setFreePlayError] = useState<string | null>(null);

  const fetchFreePlayStatus = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      const res = await apiClient["/api/v1/venue/free-play-times"]["get"]({
        parameters: { venue_id: selectedVenue.id },
        body: null,
      });
      if (res.code === 200) {
        setFreePlayStatus(res.body as FreePlayStatus);
      }
    } catch {
      // Non-critical, ignore
    }
  }, [selectedVenue]);

  const handleAdd30MinFreePlay = useCallback(async () => {
    if (!selectedVenue || !freePlayStatus) return;
    setFreePlayAdding(true);
    setFreePlayError(null);
    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 60 * 1000);
      const newAdhoc = [
        ...freePlayStatus.adhoc,
        { starts_at: now.toISOString(), expires_at: expiresAt.toISOString() },
      ];
      const res = await apiClient["/api/v1/venue/free-play-times"]["put"]({
        parameters: { venue_id: selectedVenue.id },
        body: {
          timezone: freePlayStatus.timezone,
          recurring: freePlayStatus.recurring,
          adhoc: newAdhoc,
        },
      });
      if (res.code === 200) {
        setFreePlayStatus(res.body as FreePlayStatus);
      } else {
        setFreePlayError("Failed to start free play");
      }
    } catch {
      setFreePlayError("Failed to start free play");
    } finally {
      setFreePlayAdding(false);
    }
  }, [selectedVenue, freePlayStatus]);

  const fetchDevices = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      const res = await apiClient["/api/v1/venue/devices"]["get"]({
        parameters: { venue_id: selectedVenue.id },
        body: null,
      });
      if (res.code === 200) {
        setDevices(res.body as VenueDevice[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [selectedVenue]);

  const fetchRecentSessions = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      const res = await apiClient["/api/v1/venue/sessions"]["get"]({
        parameters: { venue_id: selectedVenue.id, per_page: 5 },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as { data: RecentSession[] };
        setRecentSessions(body.data);
      }
    } catch {
      // Ignore
    } finally {
      setSessionsLoading(false);
    }
  }, [selectedVenue]);

  useEffect(() => {
    fetchDevices();
    fetchRecentSessions();
    fetchFreePlayStatus();
  }, [fetchDevices, fetchRecentSessions, fetchFreePlayStatus]);

  const handleRepaired = useCallback(() => {
    setRepairTarget(null);
    fetchDevices();
  }, [fetchDevices]);

  const handleUnpaired = useCallback(() => {
    setUnpairTarget(null);
    fetchDevices();
  }, [fetchDevices]);

  return (
    <main className="p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-bold text-text-primary mb-4 md:mb-6">Venue Dashboard</h1>

      {selectedVenue && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-xl font-bold text-text-primary">{selectedVenue.name}</h2>
            {venueStatusBadge(selectedVenue.status)}
          </div>
          <div className="text-sm text-text-muted space-y-1">
            <p>
              Slug: <span className="font-mono text-text-secondary">{selectedVenue.slug}</span>
            </p>
            <p>
              Fleet: <span className="text-text-secondary">{selectedVenue.fleet_name}</span>
            </p>
            <p>
              Fleet ID:{" "}
              <span className="font-mono text-text-secondary">{selectedVenue.fleet_id}</span>
            </p>
            {selectedVenue.address && (
              <p>
                Address: <span className="text-text-secondary">{selectedVenue.address}</span>
              </p>
            )}
            <p>
              Created:{" "}
              <span className="text-text-secondary">
                {formatVenueDate(selectedVenue.created_at)}
              </span>
            </p>
          </div>
        </div>
      )}

      {freePlayStatus && (
        <div
          className={`flex flex-col gap-4 rounded-lg border px-4 py-4 mb-6 sm:px-5 sm:flex-row sm:items-center sm:justify-between ${
            freePlayStatus.is_free_play
              ? "bg-green-500/10 border-green-500/30"
              : "bg-surface-raised border-border"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span
              className={`shrink-0 w-2.5 h-2.5 rounded-full ${freePlayStatus.is_free_play ? "bg-green-400" : "bg-gray-500"}`}
            />
            <div className="min-w-0">
              <div
                className={`text-sm font-medium ${freePlayStatus.is_free_play ? "text-green-400" : "text-text-secondary"}`}
              >
                {freePlayStatus.is_free_play ? "Free Play Active" : "Free Play Off"}
              </div>
              {freePlayStatus.is_free_play && freePlayStatus.free_play_ends_at && (
                <div className="text-sm text-text-muted">
                  {formatFreePlayRemaining(freePlayStatus.free_play_ends_at)}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:shrink-0">
            {freePlayError && <span className="text-sm text-red-400">{freePlayError}</span>}
            <button
              onClick={handleAdd30MinFreePlay}
              disabled={freePlayAdding}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-medium rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {freePlayAdding ? "Starting..." : "+ 30 Min Free Play"}
            </button>
            <a
              href="/free-play-times"
              className="text-sm text-center text-text-muted hover:text-text-secondary transition-colors no-underline"
            >
              Manage schedule
            </a>
          </div>
        </div>
      )}

      <section>
        <h2 className="text-lg font-semibold text-text-primary mb-3">📱 Devices</h2>

        {loading && <p className="text-text-muted text-sm">Loading devices...</p>}

        {!loading && devices.length === 0 && (
          <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
            <p className="text-text-secondary">No devices linked yet.</p>
            <p className="text-text-muted text-sm mt-1">
              Use the Setup Device page to link a new device.
            </p>
          </div>
        )}

        {!loading && devices.length > 0 && (
          <div className="grid gap-3">
            {devices.map((device) => (
              <div
                key={device.id}
                className="bg-surface-raised border border-border rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`shrink-0 w-2.5 h-2.5 rounded-full ${statusColor(device.status)}`}
                  />
                  <div className="min-w-0">
                    <a
                      href={`/devices/${device.id}/events`}
                      className="font-medium text-text-primary hover:text-accent transition-colors no-underline"
                    >
                      {device.name}
                    </a>
                    <div className="text-sm text-text-muted">
                      {statusLabel(device.status)}
                      <span className="sm:hidden">
                        {" "}
                        ·{" "}
                        {device.last_heartbeat_at
                          ? formatTime(device.last_heartbeat_at)
                          : "No heartbeat"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:shrink-0">
                  <span className="hidden sm:inline text-sm text-text-muted">
                    {device.last_heartbeat_at
                      ? formatTime(device.last_heartbeat_at)
                      : "No heartbeat"}
                  </span>
                  {isStale(device) && (
                    <button
                      onClick={() => setRepairTarget(device)}
                      className="flex-1 sm:flex-none text-sm px-3 py-2 sm:py-1 rounded-md border border-border text-text-secondary hover:text-accent hover:border-accent transition-colors"
                    >
                      Re-pair
                    </button>
                  )}
                  <button
                    onClick={() => setUnpairTarget(device)}
                    className="flex-1 sm:flex-none text-sm px-3 py-2 sm:py-1 rounded-md border border-border text-text-secondary hover:text-red-400 hover:border-red-400 transition-colors"
                  >
                    Unpair
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-text-primary mb-3">🎮 Recent Sessions</h2>

        {sessionsLoading && <p className="text-text-muted text-sm">Loading sessions...</p>}

        {!sessionsLoading && recentSessions.length === 0 && (
          <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
            <p className="text-text-secondary">No sessions yet</p>
          </div>
        )}

        {!sessionsLoading && recentSessions.length > 0 && (
          <div className="grid gap-2">
            {recentSessions.map((s) => (
              <div
                key={s.id}
                className="bg-surface-raised border border-border rounded-lg px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className="text-sm text-text-primary">{s.device_name}</span>
                  {sessionStatusBadge(s.status)}
                </div>
                <span className="text-sm text-text-muted">
                  {formatTime(s.starts_at ?? s.created_at)}
                </span>
              </div>
            ))}
            <a
              href="/sessions"
              className="text-sm text-accent hover:text-accent-hover transition-colors no-underline mt-1"
            >
              View all sessions &rarr;
            </a>
          </div>
        )}
      </section>

      {repairTarget && selectedVenue && (
        <RepairModal
          device={repairTarget}
          venueId={selectedVenue.id}
          onClose={() => setRepairTarget(null)}
          onRepaired={handleRepaired}
        />
      )}

      {unpairTarget && selectedVenue && (
        <UnpairModal
          device={unpairTarget}
          venueId={selectedVenue.id}
          onClose={() => setUnpairTarget(null)}
          onUnpaired={handleUnpaired}
        />
      )}
    </main>
  );
}
